/**
 * Login View - Modern Design (Cobalt ID Branding)
 * Provides Google SSO and email/password authentication via Auth0
 * Features: Clean modern UI, top-left logo, improved spacing and colors
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Typography,
  Alert,
  Container,
  Divider,
  TextField,
  InputAdornment,
  Link,
  Fade,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthContext';
import axios from '../auth/axiosConfig';
import cobalt_logo from '../cobalt_logo.svg';
import { colors } from '../styles/globals';

// Brand palette - navy to purple gradient for Cobalt ID branding
const modernGradient = '#f7f8fc';
const buttonGradient = '#0e1944';

const LoginView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginWithRedirect, logout, isLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const [hasCheckedStoredError, setHasCheckedStoredError] = useState(false);

  // Get error from navigation state or localStorage (from Auth0 context)
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      setHasCheckedStoredError(true);
    } else if (!hasCheckedStoredError) {
      const storedError = localStorage.getItem('auth_error');
      if (storedError) {
        setError(storedError);
      }
      setHasCheckedStoredError(true);
    }
  }, [location.state, hasCheckedStoredError]);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/verifications');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Check if email domain is allowed
  const checkDomainAllowed = async (emailAddress) => {
    try {
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || 'http://localhost:8010/v1';
      const response = await axios.post(`${apiBaseUrl}/auth/check-domain`, {
        email: emailAddress,
      });
      return response.data;
    } catch (error) {
      throw new Error('Failed to validate email domain. Please try again.');
    }
  };

  // Handle Google SSO
  const handleGoogleLogin = async () => {
    try {
      setError('');
      setIsSigningIn(true);
      await loginWithRedirect({
        connection: 'google-oauth2',
        prompt: 'select_account',
        max_age: 0,
      });
    } catch (err) {
      console.error('Google login error:', err);
      setError(err.message || 'Google sign-in failed. Please try again.');
      setIsSigningIn(false);
    }
  };

  // Handle email/password login
  const handleEmailLogin = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    try {
      setError('');
      setIsCheckingDomain(true);
      setIsSigningIn(true);

      const domainCheck = await checkDomainAllowed(email);

      if (!domainCheck.allowed) {
        setError(
          `Access denied: The domain "${domainCheck.domain}" is not authorized for this application. Please contact your administrator.`
        );
        setIsCheckingDomain(false);
        setIsSigningIn(false);
        return;
      }

      setIsCheckingDomain(false);
      const enterpriseAuthConnection = domainCheck.enterprise_auth_connection;

      var loginParams = {
        login_hint: email,
        screen_hint: 'login',
        prompt: 'login',
        max_age: 0,
      };

      if (enterpriseAuthConnection) {
        loginParams.connection = enterpriseAuthConnection;
      }

      await loginWithRedirect(loginParams);
    } catch (err) {
      console.error('Email login error:', err);
      setError(err.message || 'Sign-in failed. Please try again.');
      setIsCheckingDomain(false);
      setIsSigningIn(false);
    }
  };

  // Loading state
  if (isLoading || isAuthenticated) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: modernGradient,
        }}
      >
        <Fade in timeout={300}>
          <Paper
            elevation={0}
            sx={{
              px: 6,
              py: 4,
              borderRadius: 3,
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            }}
          >
            <Typography variant="h6" fontWeight={500} color="#1e3a5f">
              Loading...
            </Typography>
          </Paper>
        </Fade>
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      sx={{
        background: modernGradient,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      {/* Logo in Top Left Corner */}
      <Box
        sx={{
          position: 'fixed',
          top: 24,
          left: 24,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <img
          src={cobalt_logo}
          alt="Cobalt Logo"
          style={{
            width: '130px',              // Increased size
            marginRight: '20px',
            height: '32px',              // Increased height
            objectFsit: 'contain',
            display: 'block'
          }}
        />
      </Box>

      <Container maxWidth="sm">
        <Fade in timeout={500}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 6 },
              borderRadius: 3,
              maxWidth: 480,
              margin: '0 auto',
              border: '1px solid #d9ddf1',

            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#212121',
                  mb: 1,
                }}
              >
                Sign in to your account
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.neutral,
                  fontSize: '1rem',
                }}
              >
                Welcome back! Please enter your details.
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Fade in={!!error}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            {/* Email Form */}
            <Box component="form" onSubmit={handleEmailLogin} noValidate>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: '#334155',
                }}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={isSigningIn}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#5b7cb8', // Change from '#667eea'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5b7cb8', // Change from '#667eea'
                    },
                  },
                }}
                inputProps={{
                  autoCapitalize: 'none',
                  style: { fontSize: 16 },
                }}
              />

              {/* Primary Sign In Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSigningIn || !email || isCheckingDomain}
                sx={{
                  background: buttonGradient,
                  color: 'white',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  mb: 3,
                  '&:hover': {
                    background: buttonGradient,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    background: '#cbd5e1',
                    color: '#94a3b8',
                  },
                  transition: 'all 0.2s',
                }}
              >
                {isCheckingDomain
                  ? 'Checking Domain...'
                  : isSigningIn
                    ? 'Signing In...'
                    : 'Sign in'}
              </Button>

              {/* Divider */}
              <Divider
                sx={{
                  my: 3,
                  '&::before, &::after': {
                    borderColor: '#e2e8f0',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#94a3b8',
                    px: 2,
                    fontSize: '0.875rem',
                  }}
                >
                  OR
                </Typography>
              </Divider>

              {/* Google Sign In Button */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleGoogleLogin}
                disabled={isSigningIn}
                startIcon={
                  <img
                    src="https://developers.google.com/identity/images/g-logo.png"
                    alt="Google logo"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'white',
                      boxShadow: '0 0.5px 2px rgba(0,0,0,0.05)',
                      marginRight: 2,
                      display: 'inline-block',
                    }}
                  />
                }
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  borderColor: '#e2e8f0',
                  color: '#334155',
                  mb: 3,
                  '&:hover': {
                    borderColor: '#5b7cb8', // Changed from '#667eea'
                    background: 'rgba(91, 124, 184, 0.04)', // Changed from rgba(102, 126, 234, 0.04)
                  },
                }}
              >
                Sign in with Google
              </Button>
            </Box>

            {/* Footer Links */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '0.875rem',
                  mb: 2,
                }}
              >
                Google Sign-In is only available if it has been enabled by your administrator.
              </Typography>
            </Box>
          </Paper>
        </Fade>

        {/* Bottom Text */}
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            color: '#858ba3',
            mt: 4,
          }}
        >
          Need access? Please email info@cobaltid.com
        </Typography>

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: '#858ba3',
            mt: 1,
          }}
        >
          © {new Date().getFullYear()} Cobalt ID, Inc.
        </Typography>
      </Container>
    </Box>
  );
};

export default LoginView;